import { useEffect } from 'react';
import { platform } from '../lib/native';

/** Below this the shrink is a URL bar collapsing, not a keyboard. */
const KEYBOARD_MIN_HEIGHT = 140;

/**
 * Web equivalent of the Capacitor keyboard listeners in `lib/native.js`.
 *
 * Both write the same two things — `--keyboard-height` on the root and
 * `keyboard-visible` on the body — so every surface that lifts above the
 * keyboard (comment composer, sheets, floating panels) keeps one contract
 * instead of one per platform. Native stays the owner where it exists; this
 * only runs in the browser, where the visual viewport is the sole signal.
 *
 * Call once at the app root.
 */
export const useVisualViewportKeyboard = () => {
  useEffect(() => {
    if (platform.isNative) return undefined;
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const sync = () => {
      // Excludes the part scrolled out of view, so a page pushed up by an
      // opening keyboard does not read as extra keyboard height.
      const occluded = window.innerHeight - viewport.height - viewport.offsetTop;
      const height = occluded > KEYBOARD_MIN_HEIGHT ? Math.round(occluded) : 0;
      document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
      document.body.classList.toggle('keyboard-visible', height > 0);
    };

    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
      document.body.classList.remove('keyboard-visible');
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    };
  }, []);
};

export default useVisualViewportKeyboard;
